import React, { useEffect, useState, createElement } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Brain, Flame, Target, Compass, CheckCircle2, XCircle, ArrowRight, Smartphone, Tablet, Monitor, ChevronRight, X } from 'lucide-react';
// Reusable animation wrapper
const FadeIn = ({
  children,
  delay = 0,
  className = ''
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => <motion.div initial={{
  opacity: 0,
  y: 30
}} whileInView={{
  opacity: 1,
  y: 0
}} viewport={{
  once: true,
  margin: '-100px'
}} transition={{
  duration: 0.7,
  delay,
  ease: 'easeOut'
}} className={className} data-id="element-0">
    {children}
  </motion.div>;
export function App() {
  const [modalContent, setModalContent] = useState<'privacy' | 'terms' | null>(null);
  useEffect(() => {
    // Manual Meta Pixel initialization
    const f = window as any;
    if (f.fbq) return;
    const n = (f.fbq = function (...args: any[]) {
      n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
    }) as any;
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [] as any[];
    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    document.head.appendChild(script);
    f.fbq('init', '1272633957817634');
    f.fbq('track', 'PageView');
  }, []);
  const handleAddToCart = () => {
    const f = window as any;
    if (f.fbq) {
      f.fbq('track', 'AddToCart', {
        content_name: 'Monk Mode 30D Reset',
        content_type: 'product',
        value: 6.39,
        currency: 'USD'
      });
    }
  };
  return <div className="min-h-screen bg-[#0a0a0a] text-zinc-300 selection:bg-[#d4a853] selection:text-black font-sans" data-id="element-1">
      {/* 1. HEADER */}
      <header className="w-full py-6 border-b border-zinc-800/50 flex justify-center" data-id="element-2">
        <FadeIn data-id="element-3">
          <span className="text-zinc-500 tracking-widest text-sm uppercase font-semibold letter-spacing-2" data-id="element-4">
            La Oficina de Monk Mode Lifestyle
          </span>
        </FadeIn>
      </header>

      <main className="max-w-4xl mx-auto px-6 sm:px-8 pb-32" data-id="element-5">
        {/* 2 & 3. HEADLINE SECTION */}
        <section className="pt-20 pb-16 text-center" data-id="element-6">
          <FadeIn data-id="element-7">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.1] mb-8 font-serif" data-id="element-8">
              EL FIN DE LA PROCASTINACIÓN <br className="hidden md:block" data-id="element-9" />
              <span className="text-[#d4a853] italic" data-id="element-10">
                Y EL INICIO DE TU DOMINIO
              </span>
            </h1>
          </FadeIn>

          <FadeIn delay={0.15} data-id="element-11">
            <a href="https://entermonk.com/checkouts/cn/hWNB6E8bP8KFWhu7uqpctx3K/es-us?_r=AQAB4UGGSJlKsT383gheEYFgqNYqaksjbUJnItzpcewi78s&auto_redirect=false&cart_link_id=7e5Axvbn&edge_redirect=true&skip_shop_pay=true" target="_blank" rel="noopener noreferrer" onClick={handleAddToCart} className="group inline-flex items-center justify-center gap-3 bg-[#d4a853] hover:bg-[#e5b964] text-black font-bold text-lg md:text-xl px-8 py-5 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_-10px_rgba(212,168,83,0.5)] mb-10" data-id="element-12">
              <span data-id="element-13">EMPEZAR MI PLAN</span>
              <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-14" />
            </a>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-15">
            <div className="inline-block text-left bg-zinc-900/50 border border-zinc-800 p-6 rounded-lg max-w-2xl mx-auto" data-id="element-16">
              <p className="text-sm md:text-base text-zinc-400 mb-2" data-id="element-17">
                <strong className="text-[#d4a853]" data-id="element-18">DE:</strong> La Oficina de
                Monk Mode Lifestyle
              </p>
              <p className="text-sm md:text-base text-zinc-400" data-id="element-19">
                <strong className="text-[#d4a853]" data-id="element-20">PARA:</strong> El hombre o
                mujer que sabe que nació para algo más grande, pero se siente
                "atrapado" en su propia rutina.
              </p>
            </div>
          </FadeIn>
        </section>

        {/* 4. EMPATHY/PAIN SECTION */}
        <section className="py-16 max-w-3xl mx-auto text-lg md:text-xl leading-relaxed space-y-8" data-id="element-21">
          <FadeIn data-id="element-22">
            <p className="font-serif text-2xl text-white" data-id="element-23">Querido amigo/a,</p>
          </FadeIn>
          <FadeIn data-id="element-24">
            <p data-id="element-25">
              Si estás leyendo esto, es probable que sientas que estás operando
              al 20% de tu capacidad real.
            </p>
          </FadeIn>
          <FadeIn data-id="element-26">
            <p data-id="element-27">
              Tal vez te despiertas con la intención de "comerte el mundo", pero
              para las 10:00 AM ya has perdido una hora haciendo scroll infinito
              en TikTok.
            </p>
          </FadeIn>
          <FadeIn data-id="element-28">
            <p data-id="element-29">
              Tal vez te miras al espejo y no reconoces ese cuerpo inflamado,
              cansado y sin energía.
            </p>
          </FadeIn>
          <FadeIn data-id="element-30">
            <p data-id="element-31">
              O peor aún... tal vez sientes que tu conexión con Dios es solo un
              murmullo lejano porque el ruido de las notificaciones, el estrés
              del trabajo y los malos hábitos han silenciado tu espíritu.
            </p>
          </FadeIn>
          <FadeIn data-id="element-32">
            <p className="text-white font-medium" data-id="element-33">
              Sé exactamente cómo se siente.
            </p>
          </FadeIn>
          <FadeIn data-id="element-34">
            <p className="italic text-zinc-400 border-l-2 border-[#d4a853] pl-6 py-2" data-id="element-35">
              Se siente como estar corriendo en una cinta de correr: haces mucho
              esfuerzo, sudas, te cansas... pero no te mueves ni un centímetro
              hacia tus metas.
            </p>
          </FadeIn>
        </section>

        {/* 5. PROBLEM SECTION */}
        <section className="py-20 my-12 relative" data-id="element-36">
          <div className="absolute inset-0 bg-zinc-900/30 rounded-3xl -z-10" data-id="element-37"></div>
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center" data-id="element-38">
            <FadeIn data-id="element-39">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-10 font-serif leading-tight" data-id="element-40">
                EL PROBLEMA NO ES TU FALTA DE VOLUNTAD.
                <br data-id="element-41" />
                <span className="text-red-500/90" data-id="element-42">
                  EL PROBLEMA ES TU BIOLOGÍA.
                </span>
              </h2>
            </FadeIn>

            <div className="space-y-6 text-lg text-left text-zinc-300" data-id="element-43">
              <FadeIn delay={0.1} data-id="element-44">
                <p data-id="element-45">
                  Vivimos en la era de la "Dopamina Barata". Las corporaciones
                  gastan miles de millones para mantenerte adicto, inflamado y
                  distraído.
                </p>
              </FadeIn>
              <ul className="space-y-4 py-4" data-id="element-46">
                <FadeIn delay={0.2} data-id="element-47">
                  <li className="flex items-start gap-3" data-id="element-48">
                    <XCircle className="w-6 h-6 text-red-500/70 shrink-0 mt-1" data-id="element-49" />
                    <span data-id="element-50">
                      Si tu cuerpo está lleno de azúcar y ultraprocesados,{' '}
                      <strong data-id="element-51">no puedes tener claridad mental.</strong>
                    </span>
                  </li>
                </FadeIn>
                <FadeIn delay={0.3} data-id="element-52">
                  <li className="flex items-start gap-3" data-id="element-53">
                    <XCircle className="w-6 h-6 text-red-500/70 shrink-0 mt-1" data-id="element-54" />
                    <span data-id="element-55">
                      Si tu mente está saltando de video en video cada 15
                      segundos, <strong data-id="element-56">no puedes tener disciplina.</strong>
                    </span>
                  </li>
                </FadeIn>
                <FadeIn delay={0.4} data-id="element-57">
                  <li className="flex items-start gap-3" data-id="element-58">
                    <XCircle className="w-6 h-6 text-red-500/70 shrink-0 mt-1" data-id="element-59" />
                    <span data-id="element-60">
                      Si tu espíritu está desconectado de la gratitud y la fe,{' '}
                      <strong data-id="element-61">no tienes propósito.</strong>
                    </span>
                  </li>
                </FadeIn>
              </ul>
              <FadeIn delay={0.5} data-id="element-62">
                <p className="text-xl text-white font-serif italic text-center mt-8" data-id="element-63">
                  "Intentar tener éxito sin limpiar estos tres pilares es como
                  intentar conducir un Ferrari con el freno de mano puesto y el
                  tanque lleno de arena."
                </p>
              </FadeIn>
            </div>
          </div>
        </section>

        {/* 6. PRODUCT REVEAL */}
        <section className="py-20 text-center" data-id="element-64">
          <FadeIn data-id="element-65">
            <p className="text-[#d4a853] tracking-[0.2em] uppercase text-sm font-bold mb-4" data-id="element-66">
              Presentando
            </p>
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-6 font-serif" data-id="element-67">
              MONK MODE 30D RESET
            </h2>
            <p className="text-xl text-zinc-400 mb-12 font-light tracking-wide" data-id="element-68">
              Mente, Cuerpo, Espíritu y Alimentación.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-69">
            <div className="relative max-w-2xl mx-auto mb-16" data-id="element-70">
              <div className="absolute inset-0 bg-[#d4a853]/10 blur-3xl rounded-full -z-10" data-id="element-71"></div>
              <img src="https://cdn.magicpatterns.com/uploads/dywYeSowFRHUH2DpRUHuuZ/heylloyd_Monk_Mode_30_dias.png" alt="Monk Mode 30D Reset Mockup" className="w-full h-auto rounded-xl shadow-2xl shadow-black/50 border border-zinc-800/50" data-id="element-72" />
            </div>
          </FadeIn>

          <div className="max-w-3xl mx-auto text-lg space-y-6 text-left" data-id="element-73">
            <FadeIn data-id="element-74">
              <p data-id="element-75">
                Este no es un "librito" de motivación. Es un{' '}
                <strong data-id="element-76">sistema de choque de 30 días</strong> diseñado para
                desmantelar tus malos hábitos y reconstruir tu identidad desde
                cero.
              </p>
            </FadeIn>
            <FadeIn data-id="element-77">
              <p data-id="element-78">
                He tomado los principios más poderosos del biohacking, la
                nutrición antiinflamatoria, la psicología del hábito y la fe
                espiritual para crear el{' '}
                <strong data-id="element-79">Protocolo de Alineación Triple (PAT)</strong>.
              </p>
            </FadeIn>
          </div>
        </section>

        {/* 7. BENEFITS SECTION */}
        <section className="py-20 border-t border-zinc-800/50" data-id="element-80">
          <FadeIn data-id="element-81">
            <h3 className="text-3xl md:text-4xl font-bold text-center text-white mb-16 font-serif" data-id="element-82">
              ¿QUÉ VAS A LOGRAR EN LOS PRÓXIMOS 30 DÍAS?
            </h3>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-83">
            {[{
            icon: <Brain className="w-8 h-8 text-[#d4a853]" data-id="element-84" />,
            title: 'RESETEAR TU CEREBRO',
            desc: 'Eliminaremos la niebla mental para que puedas concentrarte en una tarea durante horas sin sentir la necesidad de mirar el teléfono.'
          }, {
            icon: <Flame className="w-8 h-8 text-[#d4a853]" data-id="element-85" />,
            title: 'DESINFLAMAR TU CUERPO',
            desc: 'Con nuestra guía de alimentación, verás cómo tu abdomen se deshincha y tu energía se dispara sin pasar hambre.'
          }, {
            icon: <Target className="w-8 h-8 text-[#d4a853]" data-id="element-86" />,
            title: 'CONSTRUIR DISCIPLINA DE ACERO',
            desc: "No más 'mañana empiezo'. El sistema de checklists diarias te obliga a ganar el día, todos los días."
          }, {
            icon: <Compass className="w-8 h-8 text-[#d4a853]" data-id="element-87" />,
            title: 'CONECTAR CON TU PROPÓSITO',
            desc: 'A través de la oración y la reflexión dirigida, volverás a escuchar lo que Dios quiere de ti.'
          }].map((benefit, idx) => <FadeIn key={idx} delay={idx * 0.1} data-id="element-88">
                <div className="bg-zinc-900/40 border border-zinc-800/80 p-8 rounded-2xl hover:bg-zinc-900/60 transition-colors h-full" data-id="element-89">
                  <div className="bg-black/50 w-16 h-16 rounded-xl flex items-center justify-center mb-6 border border-zinc-800" data-id="element-90">
                    {benefit.icon}
                  </div>
                  <h4 className="text-xl font-bold text-white mb-3 font-serif" data-id="element-91">
                    {benefit.title}
                  </h4>
                  <p className="text-zinc-400 leading-relaxed" data-id="element-92">
                    {benefit.desc}
                  </p>
                </div>
              </FadeIn>)}
          </div>
        </section>

        {/* 8. WHAT YOU GET SECTION */}
        <section id="precio" className="py-20 scroll-mt-8" data-id="element-93">
          <FadeIn data-id="element-94">
            <div className="bg-gradient-to-b from-zinc-900 to-black border border-[#d4a853]/20 rounded-3xl p-8 md:p-12 shadow-2xl shadow-[#d4a853]/5 relative overflow-hidden" data-id="element-95">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#d4a853] to-transparent opacity-50" data-id="element-96"></div>

              <h3 className="text-3xl font-bold text-center text-white mb-12 font-serif" data-id="element-97">
                ESTO ES LO QUE ESTÁS ADQUIRIENDO HOY:
              </h3>

              <div className="space-y-6 max-w-2xl mx-auto" data-id="element-98">
                {[{
                title: 'El Manual Maestro Monk Mode 30D',
                desc: 'Tu hoja de ruta paso a paso.',
                value: '$47'
              }, {
                title: 'Guía Nutricional Anti-Inflamatoria',
                desc: 'Qué comer y qué evitar para sanar tu metabolismo.',
                value: '$27'
              }, {
                title: 'Plan de Fitness 3-2-1',
                desc: 'Entrenamientos cortos para cuerpos reales y personas ocupadas.',
                value: '$25'
              }, {
                title: 'El Habit Tracker Digital/Imprimible',
                desc: 'Tu brújula diaria para no fallar.',
                value: '$15'
              }].map((item, idx) => <div key={idx} className="flex items-start gap-4 pb-6 border-b border-zinc-800/50 last:border-0" data-id="element-99">
                    <CheckCircle2 className="w-6 h-6 text-[#d4a853] shrink-0 mt-1" data-id="element-100" />
                    <div className="flex-1" data-id="element-101">
                      <h4 className="text-lg font-bold text-white" data-id="element-102">
                        {item.title}
                      </h4>
                      <p className="text-zinc-400 text-sm mt-1" data-id="element-103">{item.desc}</p>
                    </div>
                    <div className={`font-mono text-sm whitespace-nowrap line-through text-zinc-600 decoration-red-500/70`} data-id="element-104">
                      Valor: {item.value}
                    </div>
                  </div>)}

                <div className="flex items-start gap-4 pt-4 pb-6 border-b border-zinc-800/50" data-id="element-105">
                  <div className="bg-[#d4a853] text-black text-xs font-bold px-2 py-1 rounded uppercase tracking-wider shrink-0 mt-1" data-id="element-106">
                    Bonus
                  </div>
                  <div className="flex-1" data-id="element-107">
                    <h4 className="text-lg font-bold text-white" data-id="element-108">
                      Guía de Oración y Gratitud
                    </h4>
                    <p className="text-zinc-400 text-sm mt-1" data-id="element-109">
                      El combustible espiritual para tu transformación.
                    </p>
                  </div>
                  <div className="text-[#d4a853] font-mono text-sm whitespace-nowrap font-bold" data-id="element-110">
                    Gratis
                  </div>
                </div>
              </div>

              <div className="mt-12 text-center" data-id="element-111">
                <p className="text-zinc-500 text-lg mb-2" data-id="element-112">
                  TOTAL VALOR REAL:{' '}
                  <span className="line-through decoration-red-500/50" data-id="element-113">
                    $114.00
                  </span>
                </p>
                <p className="text-2xl md:text-3xl font-bold text-white font-serif mb-1" data-id="element-114">
                  Precio regular:{' '}
                  <span className="line-through decoration-red-500/50 text-zinc-500" data-id="element-115">
                    $27.00
                  </span>
                </p>
                <p className="text-4xl md:text-5xl font-bold text-white font-serif mt-4" data-id="element-116">
                  PRECIO DE DESCUENTO:{' '}
                  <span className="text-[#d4a853]" data-id="element-117">$6.39</span>
                </p>
                <a href="https://entermonk.com/checkouts/cn/hWNB6E8bP8KFWhu7uqpctx3K/es-us?_r=AQAB4UGGSJlKsT383gheEYFgqNYqaksjbUJnItzpcewi78s&auto_redirect=false&cart_link_id=7e5Axvbn&edge_redirect=true&skip_shop_pay=true" target="_blank" rel="noopener noreferrer" onClick={handleAddToCart} className="group inline-flex items-center justify-center gap-3 bg-[#d4a853] hover:bg-[#e5b964] text-black font-bold text-lg md:text-xl px-8 py-5 rounded-full transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_-10px_rgba(212,168,83,0.5)] mt-8" data-id="element-118">
                  <span data-id="element-119">EMPEZAR MI PLAN</span>
                  <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-120" />
                </a>
              </div>
            </div>
          </FadeIn>
        </section>

        {/* 9. TWO OPTIONS SECTION */}
        <section className="py-20 border-t border-zinc-800/50" data-id="element-121">
          <FadeIn data-id="element-122">
            <h3 className="text-3xl font-bold text-center text-white mb-16 font-serif" data-id="element-123">
              TIENES DOS OPCIONES...
            </h3>
          </FadeIn>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-124">
            <FadeIn delay={0.1} data-id="element-125">
              <div className="bg-zinc-900/30 border border-zinc-800 p-8 rounded-2xl opacity-70 hover:opacity-100 transition-opacity" data-id="element-126">
                <div className="text-zinc-500 font-bold mb-4 uppercase tracking-wider text-sm" data-id="element-127">
                  Opción A
                </div>
                <h4 className="text-xl font-bold text-white mb-4 font-serif" data-id="element-128">
                  Seguir igual
                </h4>
                <p className="text-zinc-400 leading-relaxed" data-id="element-129">
                  Cerrar esta página, seguir haciendo lo que has hecho hasta
                  ahora y esperar resultados diferentes (spoiler: no van a
                  llegar). Seguirás sintiéndote inflamado, distraído y
                  desconectado.
                </p>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-130">
              <div className="bg-[#d4a853]/5 border border-[#d4a853]/30 p-8 rounded-2xl relative overflow-hidden" data-id="element-131">
                <div className="absolute top-0 right-0 w-32 h-32 bg-[#d4a853]/10 blur-2xl rounded-full -z-10" data-id="element-132"></div>
                <div className="text-[#d4a853] font-bold mb-4 uppercase tracking-wider text-sm" data-id="element-133">
                  Opción B
                </div>
                <h4 className="text-xl font-bold text-white mb-4 font-serif" data-id="element-134">
                  El Reset
                </h4>
                <p className="text-zinc-300 leading-relaxed" data-id="element-135">
                  Invertir menos de lo que cuesta una cena fuera en un sistema
                  probado que va a resetear tu vida. Son solo 30 días. Tu futuro
                  yo te lo va a agradecer profundamente.
                </p>
              </div>
            </FadeIn>
          </div>
        </section>
      </main>

      {/* 11. FOOTER */}
      <footer className="border-t border-zinc-900 py-8 text-center text-zinc-600 text-sm" data-id="element-136">
        <p className="mb-3" data-id="element-137">
          © {new Date().getFullYear()} Monk Mode Lifestyle. Todos los derechos
          reservados.
        </p>
        <div className="flex items-center justify-center gap-6" data-id="element-138">
          <button onClick={() => setModalContent('privacy')} className="hover:text-[#d4a853] transition-colors underline underline-offset-2" data-id="element-139">
            Política de Privacidad
          </button>
          <span className="text-zinc-800" data-id="element-140">|</span>
          <button onClick={() => setModalContent('terms')} className="hover:text-[#d4a853] transition-colors underline underline-offset-2" data-id="element-141">
            Términos y Condiciones
          </button>
        </div>
      </footer>

      {/* LEGAL MODALS */}
      <AnimatePresence data-id="element-142">
        {modalContent && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setModalContent(null)} data-id="element-143">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.3
        }} className="bg-zinc-900 border border-zinc-800 rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto p-8 relative" onClick={e => e.stopPropagation()} data-id="element-144">
              <button onClick={() => setModalContent(null)} className="absolute top-4 right-4 text-zinc-500 hover:text-white transition-colors" data-id="element-145">
                <X className="w-6 h-6" data-id="element-146" />
              </button>
              <h3 className="text-2xl font-bold text-white font-serif mb-6" data-id="element-147">
                {modalContent === 'privacy' ? 'Política de Privacidad' : 'Términos y Condiciones'}
              </h3>
              <div className="text-zinc-400 text-sm leading-relaxed space-y-4" data-id="element-148">
                {modalContent === 'privacy' ? <PrivacyContent data-id="element-149" /> : <TermsContent data-id="element-150" />}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
function PrivacyContent() {
  return <>
      <p data-id="element-151">
        <strong className="text-white" data-id="element-152">Última actualización:</strong> Enero 2025
      </p>
      <p data-id="element-153">
        En Monk Mode Lifestyle, nos comprometemos a proteger tu privacidad. Esta
        política describe cómo recopilamos, usamos y protegemos tu información
        personal.
      </p>
      <h4 className="text-white font-bold mt-4" data-id="element-154">
        1. Información que recopilamos
      </h4>
      <p data-id="element-155">
        Recopilamos información que nos proporcionas directamente al realizar
        una compra, como tu nombre, dirección de correo electrónico e
        información de pago. No almacenamos datos de tarjetas de crédito en
        nuestros servidores.
      </p>
      <h4 className="text-white font-bold mt-4" data-id="element-156">2. Uso de la información</h4>
      <p data-id="element-157">
        Utilizamos tu información para procesar tu compra, enviarte el producto
        digital adquirido y comunicarnos contigo sobre tu pedido. No vendemos ni
        compartimos tu información personal con terceros para fines de
        marketing.
      </p>
      <h4 className="text-white font-bold mt-4" data-id="element-158">3. Protección de datos</h4>
      <p data-id="element-159">
        Implementamos medidas de seguridad técnicas y organizativas para
        proteger tu información personal contra acceso no autorizado, alteración
        o destrucción.
      </p>
      <h4 className="text-white font-bold mt-4" data-id="element-160">4. Cookies</h4>
      <p data-id="element-161">
        Nuestro sitio puede utilizar cookies para mejorar tu experiencia de
        navegación y analizar el tráfico del sitio. Puedes configurar tu
        navegador para rechazar cookies.
      </p>
      <h4 className="text-white font-bold mt-4" data-id="element-162">5. Tus derechos</h4>
      <p data-id="element-163">
        Tienes derecho a acceder, corregir o eliminar tu información personal en
        cualquier momento. También puedes solicitar la portabilidad de tus
        datos.
      </p>
    </>;
}
function TermsContent() {
  return <>
      <p data-id="element-164">
        <strong className="text-white" data-id="element-165">Última actualización:</strong> Enero 2025
      </p>
      <p data-id="element-166">
        Al adquirir cualquier producto de Monk Mode Lifestyle, aceptas los
        siguientes términos y condiciones.
      </p>
      <h4 className="text-white font-bold mt-4" data-id="element-167">1. Producto digital</h4>
      <p data-id="element-168">
        Monk Mode 30D Reset es un producto digital en formato PDF. Una vez
        completada la compra, recibirás acceso inmediato al contenido. Debido a
        la naturaleza digital del producto, no se aceptan devoluciones una vez
        descargado.
      </p>
      <h4 className="text-white font-bold mt-4" data-id="element-169">2. Uso personal</h4>
      <p data-id="element-170">
        El contenido adquirido es para uso personal exclusivamente. Queda
        prohibida la reproducción, distribución o reventa del material sin
        autorización expresa.
      </p>
      <h4 className="text-white font-bold mt-4" data-id="element-171">3. Resultados</h4>
      <p data-id="element-172">
        Los resultados pueden variar de persona a persona. Monk Mode Lifestyle
        no garantiza resultados específicos. El éxito depende del compromiso y
        la dedicación individual de cada usuario.
      </p>
      <h4 className="text-white font-bold mt-4" data-id="element-173">
        4. Descargo de responsabilidad médica
      </h4>
      <p data-id="element-174">
        El contenido de este programa es informativo y educativo. No sustituye
        el consejo médico profesional. Consulta con un profesional de la salud
        antes de realizar cambios significativos en tu dieta o rutina de
        ejercicios.
      </p>
      <h4 className="text-white font-bold mt-4" data-id="element-175">5. Propiedad intelectual</h4>
      <p data-id="element-176">
        Todo el contenido, incluyendo textos, gráficos, diseños y materiales, es
        propiedad de Monk Mode Lifestyle y está protegido por las leyes de
        propiedad intelectual.
      </p>
    </>;
}